import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';

export interface ObjectFieldDefinition {
  name: string;
  display_name?: string | null;
  field_type: string;
  array_item_type?: string | null;
  is_required?: boolean;
  help_text?: string | null;
  description?: string | null;
  allowed_values?: string[] | null;
  reference_object_id?: string | null;
  nested_fields?: ObjectFieldDefinition[] | null;
  [key: string]: unknown;
}

export type FieldChangeKind = 'added' | 'removed' | 'changed';

interface ObjectFieldTreeProps {
  fields: ObjectFieldDefinition[];
  /** Keyed by field path: `parent.child` for objects, `parent[].child` for array items. */
  highlights?: Record<string, FieldChangeKind>;
  /** Object id to display name, used to label reference fields. */
  referenceNames?: Record<string, string>;
  emptyMessage?: string;
}

export const fieldTypeLabel = (field: Pick<ObjectFieldDefinition, 'field_type' | 'array_item_type'>): string =>
  field.field_type === 'array' ? `array<${field.array_item_type || 'string'}>` : field.field_type;

const TYPE_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning'> = {
  string: 'primary',
  text: 'primary',
  integer: 'info',
  float: 'info',
  boolean: 'success',
  date: 'warning',
  datetime: 'warning',
  uuid: 'default',
  json: 'secondary',
  array: 'secondary',
  reference: 'secondary',
};

const HIGHLIGHT_COLORS: Record<FieldChangeKind, string> = {
  added: 'success.main',
  changed: 'warning.main',
  removed: 'error.main',
};

interface FieldRowProps {
  field: ObjectFieldDefinition;
  path: string;
  highlights?: Record<string, FieldChangeKind>;
  referenceNames?: Record<string, string>;
}

const FieldRow: React.FC<FieldRowProps> = ({ field, path, highlights, referenceNames }) => {
  const highlight = highlights?.[path];
  const children = field.nested_fields || [];
  const isArray = field.field_type === 'array';
  const childPrefix = `${path}${isArray ? '[].' : '.'}`;
  const helpText = field.help_text || field.description;
  const referenceName = field.reference_object_id
    ? referenceNames?.[field.reference_object_id] || field.reference_object_id
    : null;
  const showDisplayName = field.display_name && field.display_name.toLowerCase().replace(/\s+/g, '_') !== field.name;

  return (
    <Box
      sx={{
        py: 1,
        pl: 1.5,
        borderLeft: 3,
        borderColor: highlight ? HIGHLIGHT_COLORS[highlight] : 'transparent',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
          {field.name}
        </Typography>
        <Chip
          label={fieldTypeLabel(field)}
          size="small"
          color={TYPE_COLORS[field.field_type] || 'default'}
          variant="outlined"
          sx={{ fontFamily: 'monospace', height: 22 }}
        />
        {field.is_required && (
          <Chip label="required" size="small" color="error" variant="outlined" sx={{ height: 22 }} />
        )}
        {highlight === 'added' && <Chip label="new" size="small" color="success" sx={{ height: 22 }} />}
        {highlight === 'changed' && <Chip label="changed" size="small" color="warning" sx={{ height: 22 }} />}
        {showDisplayName && (
          <Typography component="span" variant="caption" color="text.secondary">
            {field.display_name}
          </Typography>
        )}
      </Stack>
      {helpText && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {helpText}
        </Typography>
      )}
      {referenceName && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Links to: {referenceName}
        </Typography>
      )}
      {field.allowed_values && field.allowed_values.length > 0 && (
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            One of:
          </Typography>
          {field.allowed_values.map((value) => (
            <Chip key={value} label={value} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
          ))}
        </Stack>
      )}
      {children.length > 0 && (
        <Box sx={{ mt: 0.5, ml: 1, pl: 1, borderLeft: '1px dashed', borderColor: 'divider' }}>
          {isArray && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 1.5 }}>
              Each item has:
            </Typography>
          )}
          {children.map((child) => (
            <FieldRow
              key={child.name}
              field={child}
              path={`${childPrefix}${child.name}`}
              highlights={highlights}
              referenceNames={referenceNames}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export const ObjectFieldTree: React.FC<ObjectFieldTreeProps> = ({
  fields,
  highlights,
  referenceNames,
  emptyMessage = 'No fields',
}) => {
  if (!fields.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyMessage}
      </Typography>
    );
  }
  return (
    <Box>
      {fields.map((field) => (
        <FieldRow
          key={field.name}
          field={field}
          path={field.name}
          highlights={highlights}
          referenceNames={referenceNames}
        />
      ))}
    </Box>
  );
};

export default ObjectFieldTree;
